// Secondary feature permissions (user_permissions table).
// Independent of primary user_profiles.role.

import { supabase } from '../lib/supabase';

export const PERMISSION_KEYS = {
  INTERNAL_FACTORY_DIRECTORY: 'internal_factory_directory',
  DO_FINANCE: 'do_finance',
};

/**
 * Load enabled map for a single permission_key across many users.
 * @returns {Promise<Record<string, boolean>>} user_id -> enabled
 */
export const getPermissionMapForUsers = async (permissionKey, userIds = []) => {
  if (!permissionKey || !userIds?.length) return {};

  const { data, error } = await supabase
    ?.from('user_permissions')
    ?.select('user_id, enabled')
    ?.eq('permission_key', permissionKey)
    ?.in('user_id', userIds);

  if (error) {
    console.error('getPermissionMapForUsers error:', error?.message);
    return {};
  }

  const map = {};
  (data || []).forEach((row) => {
    map[row.user_id] = !!row.enabled;
  });
  return map;
};

/**
 * Whether the given user has a permission enabled.
 */
export const hasPermission = async (userId, permissionKey) => {
  if (!userId || !permissionKey) return false;

  const { data, error } = await supabase
    ?.from('user_permissions')
    ?.select('enabled')
    ?.eq('user_id', userId)
    ?.eq('permission_key', permissionKey)
    ?.maybeSingle();

  if (error) {
    console.error('hasPermission error:', error?.message);
    return false;
  }
  return !!data?.enabled;
};

/**
 * Upsert a permission flag (admin write).
 */
export const setPermission = async (userId, permissionKey, enabled) => {
  if (!userId || !permissionKey) {
    return { error: { message: 'userId and permissionKey required' } };
  }

  const { data, error } = await supabase
    ?.from('user_permissions')
    ?.upsert(
      {
        user_id: userId,
        permission_key: permissionKey,
        enabled: !!enabled,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,permission_key' }
    )
    ?.select('user_id, permission_key, enabled')
    ?.maybeSingle();

  return { data, error };
};
